import DividerWithText from "../../../../components/DividerWithText";
import DivContainerBody from "../../../../components/DivContainerBody";

import {
    Box,
    InputAdornment,
    MenuItem
} from "@mui/material";
import React from "react";
import {CustomTextField} from "../../../../components/CustomTextField";
import {CustomSelect} from "../../../../components/CustomSelect";
import {useSelector} from "react-redux";
import {CustomSelectAndChildren} from "../../../../components/CustomSelectAndChildren";
import {CustomCheckbox} from "../../../../components/CustomCheckbox";
import {ANTIPASSBACKTYPE_MODEL, DEVICE_MODEL_NAME} from "../Model/Model";

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 *
 * SectionCreateDeviceSecurity Component - Security configuration section for device management.
 * Handles advanced security settings including anti-passback systems, escort modes, biometric checks,
 * and device synchronization. Features complex conditional logic based on device type and state.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} data - Device security configuration data
 * @param {Function} handleChange - Universal change handler
 * @param {Function} t - Translation function
 * @param {boolean} formSubmitted - Form submission status
 * @param {boolean} emptyFields - Validation state flag
 * @returns {JSX.Element}
 * @constructor
 */


const SectionCreateDeviceSecurity = ({data, handleChange, t, formSubmitted, emptyFields}) => {

    // Conditional control arrays
    const disabledDeviceTypeAllowed = [
        DEVICE_MODEL_NAME.TELPO_F6,
        DEVICE_MODEL_NAME.TELPO_K5,
        DEVICE_MODEL_NAME.TELPO_F10,
        DEVICE_MODEL_NAME.TELPO_F6_T,
        DEVICE_MODEL_NAME.TELPO_K10,
        DEVICE_MODEL_NAME.ARATEK_BM7550,
        DEVICE_MODEL_NAME.ARATEK_BA8300].includes(data.deviceType);

    const disabledAntiPassBack = [
        DEVICE_MODEL_NAME.VERIDAS_1FA,
        DEVICE_MODEL_NAME.VERIDAS_2FA,
        DEVICE_MODEL_NAME.ANTENNA_RFID,
        DEVICE_MODEL_NAME.CAMERA_LPR].includes(data.deviceType);

    const disabledAntiPassBackTime = [
        DEVICE_MODEL_NAME.VERIDAS_1FA,
        DEVICE_MODEL_NAME.VERIDAS_2FA,
        DEVICE_MODEL_NAME.ANTENNA_RFID,
        DEVICE_MODEL_NAME.CAMERA_LPR].includes(data.deviceType) || data.antiPassBackType === ANTIPASSBACKTYPE_MODEL.NONE;

    const disabledAntiPassBackAlert = data.antiPassBackType === ANTIPASSBACKTYPE_MODEL.NONE
        || [
            DEVICE_MODEL_NAME.VERIDAS_1FA,
            DEVICE_MODEL_NAME.VERIDAS_2FA,
            DEVICE_MODEL_NAME.ANTENNA_RFID,
            DEVICE_MODEL_NAME.CAMERA_LPR].includes(data.deviceType);

    const disabledRequestSignatureAlways = [
        DEVICE_MODEL_NAME.TELPO_K5,
        DEVICE_MODEL_NAME.TELPO_K10].includes(data.deviceType);

    const disabledRequestSignature = [
        DEVICE_MODEL_NAME.TELPO_K5,
        DEVICE_MODEL_NAME.TELPO_K10].includes(data.deviceType);

    const disabledSyncTimeValue = [
        DEVICE_MODEL_NAME.VERIDAS_1FA,
        DEVICE_MODEL_NAME.VERIDAS_2FA,
        DEVICE_MODEL_NAME.ANTENNA_RFID,
        DEVICE_MODEL_NAME.CAMERA_LPR].includes(data.deviceType);

    // Validation states
    const emptyAntiPassBack = emptyFields && ![
        DEVICE_MODEL_NAME.VERIDAS_1FA,
        DEVICE_MODEL_NAME.VERIDAS_2FA,
        DEVICE_MODEL_NAME.ANTENNA_RFID,
        DEVICE_MODEL_NAME.CAMERA_LPR].includes(data.deviceType);
    const emptyAntiPassBackTime = formSubmitted
        && emptyFields
        && ![
            DEVICE_MODEL_NAME.VERIDAS_1FA,
            DEVICE_MODEL_NAME.VERIDAS_2FA,
            DEVICE_MODEL_NAME.ANTENNA_RFID,
            DEVICE_MODEL_NAME.CAMERA_LPR].includes(data.deviceType)
        && (data.antiPassBackType !== 1 && (!data.antiPassBackTime || data.antiPassBackTime <= 0));

    const emptyAntiPassBackAlert = emptyFields && ![
        DEVICE_MODEL_NAME.VERIDAS_1FA,
        DEVICE_MODEL_NAME.VERIDAS_2FA,
        DEVICE_MODEL_NAME.ANTENNA_RFID,
        DEVICE_MODEL_NAME.CAMERA_LPR].includes(data.deviceType)
        && data.antiPassBackType !== ANTIPASSBACKTYPE_MODEL.NONE;

    const emptyEscortMode = emptyFields && ![
        DEVICE_MODEL_NAME.VERIDAS_1FA,
        DEVICE_MODEL_NAME.VERIDAS_2FA,
        DEVICE_MODEL_NAME.ANTENNA_RFID,
        DEVICE_MODEL_NAME.CAMERA_LPR,].includes(data.deviceType);
    // Redux state access
    const {getAntiPassBackType, getEscortMode} = useSelector((state) => state.DevicesSlice);
    const {getAllMedia} = useSelector((state) => state.CommonsSlice);

    return (
        <DivContainerBody>
            <div className={"row"}>
                <div className={"col-12"}>
                    <DividerWithText title={t('security')} spacing={"mb-4"}/>
                </div>
                <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                    <CustomSelectAndChildren
                        id='slt_cud_anti_pass_back'
                        name='antiPassBackType'
                        emptyFields={emptyAntiPassBack}
                        value={data?.antiPassBackType}
                        trans='anti_pass_back'
                        required={true}
                        onChange={(e) => { handleChange(e, "antiPassBackType") }}
                        disabled={disabledAntiPassBack}
                        showNoSelectionOption={false}
                    >
                        {getAntiPassBackType?.map((item, index) => (
                            <MenuItem key={index} value={item.id}>
                                {item.id === 1 ? t('none') : item.name}
                            </MenuItem>
                        ))}
                </CustomSelectAndChildren>
                </Box>
                <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                    <CustomTextField
                        id='txt_cud_anti_bass_back_time'
                        name='antiPassBackTime'
                        emptyFields={emptyAntiPassBackTime}
                        isValid={!(data.antiPassBackType !== 1 && (!data.antiPassBackTime || data.antiPassBackTime <= 0))}
                        isValidText={'this_value_must_be_greater_than_0'}
                        value={data?.antiPassBackTime}
                        trans='anti_pass_back_time'
                        required={true}
                        onChange={(e) => {
                            const value = e.target.value;
                            const regex = /^[0-9]*$/;
                            if (regex.test(value) && (value === '' || parseInt(value, 10) >= 0)) {
                                handleChange(e);
                            }
                        }}
                        length={4}
                        disabled={disabledAntiPassBackTime}
                        helperText={'this_value_must_be_greater_than_0'}
                        InputProps={{
                            endAdornment: <InputAdornment position='end'>{t('sec')}</InputAdornment>,
                        }}
                    />
                </Box>
                <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                    <CustomSelectAndChildren
                        id='slt_cud_anti_pass_back_alert'
                        name='antiPassBackAlert'
                        emptyFields={emptyAntiPassBackAlert}
                        value={data?.antiPassBackAlert}
                        trans='anti_pass_back_alert_by'
                        required={true}
                        onChange={(evt) => { handleChange(evt, "antiPassBackAlert") }}
                        disabled={disabledAntiPassBackAlert}
                        showNoSelectionOption={false}
                    >
                        {getAllMedia?.map((item, index) => {
                            return <MenuItem value={item.id}>{item.name}</MenuItem>;
                        })}
                    </CustomSelectAndChildren>
                </Box>
                <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                    <CustomSelectAndChildren
                        id='slt_cud_escort'
                        name='escortMode'
                        emptyFields={emptyEscortMode}
                        value={data?.escortMode}
                        trans='escort'
                        required={true}
                        onChange={(evt) => { handleChange(evt, "escortMode") }}
                        disabled={true}
                    >
                        {getEscortMode?.map((item, index) => {
                            return (
                                <MenuItem value={item.id}>
                                    {(item?.id === 2 && t('needed').toUpperCase()) ||
                                    (item?.id === 1 && t('none').toUpperCase())}
                                </MenuItem>
                            );
                        })}
                    </CustomSelectAndChildren>
                </Box>
                <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                    <CustomSelectAndChildren
                        id='slt_cud_request_signature'
                        name='requestSignatureAlways'
                        value={data?.requestSignatureAlways}
                        trans='request_signature'
                        onChange={(evt) => { handleChange(evt, "requestSignatureAlways") }}
                        disabled={!disabledRequestSignatureAlways}
                        showNoSelectionOption={false}
                    >
                        <MenuItem value={true}>{t('YES')}</MenuItem>
                        <MenuItem value={false}>{t('NO')}</MenuItem>
                    </CustomSelectAndChildren>
                </Box>
                <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                    <CustomTextField
                        id='txt_cud_request_signature'
                        name='requestSignature'
                        value={data?.requestSignature}
                        trans='request_signature_every'
                        onChange={(e) => {
                            const value = e.target.value;
                            const regex = /^[0-9]*$/;
                            if (regex.test(value) && (value === '' || parseInt(value, 10) >= 0)) {
                                handleChange(e);
                            }
                        }}
                        length={4}
                        showCounter={false}
                        disabled={!disabledRequestSignature}
                        helperText={t('fill_0_to_make_it_everytime')}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment className='endAdornmentStyle' position='end'>
                                    {t('days')}
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
                <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                    <CustomTextField
                        id='txt_cud_sync_time'
                        name='syncTime'
                        value={data?.syncTime}
                        trans='sync_time'
                        disabled={disabledSyncTimeValue}
                        required={true}
                        emptyFields={!disabledSyncTimeValue}
                        onChange={(e) => {
                            const value = e.target.value;
                            const regex = /^[0-9]*$/;
                            if (regex.test(value) && (value === '' || parseInt(value, 10) >= 0)) {
                                handleChange(e);
                            }
                        }}
                        length={4}
                        showCounter={false}
                        helperText={t('fill_0_to_make_it_everytime')}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment className='endAdornmentStyle' position='end'>
                                    HR
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
                <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                    <CustomTextField
                        id='txt_cud_validation_number'
                        name='validationNumber'
                        value={data?.validationNumber}
                        trans='validation_number'
                        onChange={(e) => {
                            const value = e.target.value;
                            const regex = /^[0-9]*$/;
                            if (regex.test(value) && (value === '' || parseInt(value, 10) >= 0)) {
                                handleChange(e);
                            }
                        }}
                        length={4}
                        showCounter={false}
                        disabled={true}
                    />
                </Box>
                <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6 mt-3'>
                    <CustomCheckbox
                        marginbutton={true}
                        id='cb_cud_liveness'
                        checked={data.liveness}
                        onChange={(e) => handleChange(e)}
                        labelId={"lbl_cud_liveness"}
                        label={'liveness'}
                        trans={'liveness'}
                        name={'liveness'}
                        disabled={!disabledDeviceTypeAllowed}
                    />
                </Box>
                <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6 mt-3'>
                    <CustomSelectAndChildren
                        id='slt_cud_camera_type'
                        name='cameraTypeId'
                        value={data?.cameraTypeId}
                        trans='camera_type_id'
                        onChange={(evt) => { handleChange(evt, "cameraTypeId") }}
                        disabled={!data.liveness}
                        showNoSelectionOption={false}
                    >
                        <MenuItem value={0}>{t('RGB')}</MenuItem>
                        {data.deviceType !== 14 && <MenuItem value={1}>{t('RGB + IR')}</MenuItem>}
                    </CustomSelectAndChildren>
                </Box>

            </div>
        </DivContainerBody>
    );
};

export default SectionCreateDeviceSecurity;
